function numericStrategyDelta(month: MonthlyEntry, key: string): number {
  const value = (month.deltas.strategyDeltas as Record<string, unknown> | undefined)?.[key];
  return typeof value === 'number' ? value : 0;
}

function waitingPeriodDaysServed(month: MonthlyEntry): number {
  // The engine decrements both buckets for the same served days. Taking their
  // maximum preserves that one elapsed-day stream instead of counting it twice.
  return Math.max(
    month.facts.waitingPeriodDaysConsumedHome,
    month.facts.waitingPeriodDaysConsumedFacility,
  );
}

function calcEngineMaxAchievableCoverage(projection: ClaimProjection): number {
  // The quote's coverage scalar is primary-only, but a joint indemnity ledger
  // shares its surplus reserve. Scan partner rows for cash that could be banked
  // before the primary's claim without adding the partner's costs to the total.
  let structuralOpeningSurplusAvailable = false;

  return projection.months.reduce((sum, month) => {
    const isPrimaryClaimant = month.context.claimant !== 'partner';
    const epLumpFiresThisMonth = numericStrategyDelta(month, 'epLumpSumDays') > 0;
    const eligibleCareCost = isPrimaryClaimant
      ? month.facts.dailyCareCost * month.facts.coverageEligibleDays
      : 0;
    // The projection applies a retroactive lump to cost on its own row only;
    // any excess is banked for later care. A prior row from either claimant can
    // therefore make later primary waiting care structurally coverable, but
    // fresh cash from this row cannot be used as opening surplus on the same row.
    const waitingPeriodCareCost =
      isPrimaryClaimant && (epLumpFiresThisMonth || structuralOpeningSurplusAvailable)
        ? month.facts.dailyCareCost * waitingPeriodDaysServed(month)
        : 0;

    const supportsIndemnitySurplus =
      typeof (month.deltas.strategyDeltas as Record<string, unknown> | undefined)
        ?.indemnitySurplusAccrued === 'number';
    const canProduceFreshIndemnityCash =
      month.facts.coverageEligibleDays > 0 || epLumpFiresThisMonth;
    // Only fresh indemnity cash can pay the current row's room and board;
    // opening surplus remains care-only by contract with sharedSurplus.ts.
    const coverableRoomAndBoard =
      isPrimaryClaimant &&
      projection.terms.isIndemnity &&
      supportsIndemnitySurplus &&
      canProduceFreshIndemnityCash
        ? (month.facts.roomAndBoardCost ?? 0)
        : 0;

    if (projection.terms.isIndemnity && supportsIndemnitySurplus && canProduceFreshIndemnityCash) {
      structuralOpeningSurplusAvailable = true;
    }

    return sum + eligibleCareCost + waitingPeriodCareCost + coverableRoomAndBoard;
  }, 0);
}

export function calcMaxAchievableCoverage(
  updatedClient: Client,
  fundingSourceId: string,
  projectedCostCoverage: number,
): number {
  const phaseCalculations = updatedClient.policyPhaseCalculations?.[fundingSourceId];
  const phaseBasedCoverage =
    phaseCalculations?.reduce(
      (sum, phase) => sum + (phase.portionOfPhaseCostBenefitEligible ?? 0),
      0,
    ) ?? projectedCostCoverage;

  const policy = updatedClient.multipleFundingSources.ltcPolicy[fundingSourceId];
  const projection = updatedClient.policyClaimProjections?.[fundingSourceId];
  const structuralCoverage =
    policy?.policyProjectionSource === 'engine' && projection
      ? calcEngineMaxAchievableCoverage(projection)
      : phaseBasedCoverage;

  return Math.max(structuralCoverage, projectedCostCoverage);
}
